// Admin auth — talks to the signal server's /admin/auth/* endpoints.
// Session token + admin profile live in localStorage (managed by
// adminClient). This module exposes the React hook + login/logout/
// session-read helpers the rest of the panel consumes.

import { useEffect, useState } from 'react'
import {
  adminLogin as remoteLogin,
  adminLogout as remoteLogout,
  getSession,
  SESSION_CHANGE_EVENT
} from './adminClient'

export interface AdminSession {
  email: string
  name: string
  role: 'owner' | 'admin' | 'support'
  signedInAt: string
}

function payloadToSession(p: ReturnType<typeof getSession>): AdminSession | null {
  if (!p) return null
  return {
    email: p.admin.email,
    name: p.admin.name,
    role: p.admin.role,
    signedInAt: new Date(p.admin.lastLoginAt ?? p.admin.createdAt).toISOString()
  }
}

export async function login(email: string, password: string): Promise<AdminSession | null> {
  const res = await remoteLogin(email, password)
  if (!res.ok) return null
  return payloadToSession(getSession())
}

export async function logout(): Promise<void> {
  await remoteLogout()
}

export function useAuth(): AdminSession | null {
  const [session, setSession] = useState<AdminSession | null>(() =>
    payloadToSession(getSession())
  )
  useEffect(() => {
    const update = () => setSession(payloadToSession(getSession()))
    window.addEventListener('storage', update)
    window.addEventListener(SESSION_CHANGE_EVENT, update)
    return () => {
      window.removeEventListener('storage', update)
      window.removeEventListener(SESSION_CHANGE_EVENT, update)
    }
  }, [])
  return session
}
